import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Form, Row, Col, Input, Select, Checkbox, Badge } from "antd";
import { debounce, isEmpty, isEqual } from "lodash";
import { VehicleTypeMap, Drivability, VehicleType, ShipmentStatus, VehicleColor } from "@/utils/enum";
import { getTrimPackageFromVinData } from "@/utils/utils";
import { Validator } from "@/utils/validator";
import useVinDecoder from "@/utils/vinDecoder";
import ExteriorDimensionsInputs from "@/components/ExteriorDimensionsInputs";
import GatePassForm from "./GatePassForm";
import VehiclePricing from "./VehiclePricing";
import VinInput from "./VinInput";

const buildPricingEnginePayload = ({ shipmentData, vehicleData }) => ({
  vehicleType: vehicleData?.vehicleType,
  vehicleCurbWeight: vehicleData?.curbWeight ?? 0,
  vin: vehicleData?.vin,
  shipmentId: shipmentData?.id,
  distance: shipmentData?.distance,
  vehicleCount: (shipmentData?.vehicles?.length ?? 0) + 1,
  pickupZip: shipmentData?.pickupAddress?.zipCode,
  pickupCity: shipmentData?.pickupAddress?.city,
  pickupStateId: shipmentData?.pickupAddress?.stateId,
  pickupStateShortName: shipmentData?.pickupAddress?.stateShortName,
  dropOffZip: shipmentData?.dropOffAddress?.zipCode,
  dropOffCity: shipmentData?.dropOffAddress?.city,
  dropOffStateId: shipmentData?.dropOffAddress?.stateId,
  dropOffStateShortName: shipmentData?.dropOffAddress?.stateShortName,
  dealerId: shipmentData?.dealerId,
});

export default function VehicleForm({
  formRef,
  shipmentStatus, // required when editing a vehicle
  defaultValues,
  onSubmit,
  editMode = false,
  shipmentData,
  configPerm,
}) {
  const isAdmin = !!configPerm["CoreService.AdminShipments"];
  const disabledFields = !isAdmin && editMode;
  const readOnly = shipmentStatus >= ShipmentStatus.Delivered;
  const [currentVehicleData, setCurrentVehicleData] = useState({}); // store vin, make and model
  const {
    data: vinData,
    list: vinList,
    status: vinStatus,
    handleSelect,
    isOpen: trimSelectOpen,
    handleOpenChange,
    isAdminOverride,
  } = useVinDecoder({
    ...currentVehicleData,
    formRef,
    isAdmin,
  });

  const debouncedVin = useCallback(
    debounce(newVin => {
      setCurrentVehicleData(prevData => ({ ...prevData, vin: newVin }));
    }, 500),
    [],
  );

  const debouncedMake = useCallback(
    debounce(newMake => {
      setCurrentVehicleData(prevData => ({
        ...prevData,
        make: newMake,
      }));
    }, 500),
    [],
  );

  const debouncedModel = useCallback(
    debounce(newModel => {
      setCurrentVehicleData(prevData => ({
        ...prevData,
        model: newModel,
      }));
    }, 500),
    [],
  );

  const lastVehicleType = Form.useWatch("vehicleType", formRef); // watch vehicle type changes

  // update form with default values
  useEffect(() => {
    if (!isEqual(defaultValues, formRef?.getFieldsValue())) {
      formRef?.setFieldsValue(defaultValues);
    }
  }, [defaultValues, formRef]);

  // Cleanup debounced functions when component unmounts
  useEffect(
    () => () => {
      debouncedVin.cancel();
      debouncedMake.cancel();
      debouncedModel.cancel();
    },
    [debouncedVin, debouncedMake, debouncedModel],
  );

  const isPricingSectionVisible = useMemo(() => {
    const hasPermission = isAdmin && !editMode && !!configPerm["PricingService.QuoteRuleResults"];
    if (!hasPermission) return false;

    // Show if we have valid VIN data
    if (!isEmpty(vinData)) return true;

    // Show if admin override and required fields are filled
    if (isAdminOverride) {
      return !!currentVehicleData?.vin && !!lastVehicleType;
    }

    return false;
  }, [isAdmin, editMode, configPerm, vinData, isAdminOverride, lastVehicleType, currentVehicleData?.vin]);

  const requiredShipmentData = useMemo(() => {
    const vehicleData = isAdminOverride ? { vin: currentVehicleData?.vin, vehicleType: lastVehicleType } : vinData;

    return buildPricingEnginePayload({ shipmentData, vehicleData });
  }, [shipmentData, vinData, isAdminOverride, lastVehicleType, currentVehicleData?.vin]);

  return (
    <Form form={formRef} layout="vertical" initialValues={defaultValues} onFinish={onSubmit}>
      <Row gutter={16}>
        <Col xs={24} sm={24} md={24} lg={12}>
          <VinInput
            formRef={formRef}
            onChange={debouncedVin}
            disabledFields={disabledFields}
            readOnly={readOnly}
            isAdmin={isAdmin}
            vinStatus={vinStatus}
          />
        </Col>
        <Col lg={12} xs={24}>
          <Form.Item
            name="trimPackageName"
            label="Trim Package"
            style={{ display: vinList?.length <= 1 ? "block" : "none" }}
            rules={[Validator.required("Trim package is required")]}
          >
            <Input disabled={!isEmpty(vinData) || disabledFields || readOnly} style={{ color: "black" }} />
          </Form.Item>

          <Form.Item
            name="trim"
            label="Trim Package"
            hidden={vinList?.length ?? 0 <= 1}
            style={{ display: vinList?.length > 1 ? "block" : "none" }}
            rules={[vinList?.length > 1 && Validator.required("Trim package is required")]}
          >
            <Select
              placeholder="Vehicles"
              disabled={vinList?.length <= 1 || disabledFields}
              onChange={handleSelect}
              open={trimSelectOpen}
              onDropdownVisibleChange={handleOpenChange}
              autoFocus
            >
              {vinList?.map((vin, index) => {
                const value = getTrimPackageFromVinData(vin);

                return (
                  <Select.Option key={value} value={index}>
                    {value}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} sm={24} md={24} lg={6}>
          <Form.Item name="stockNumber" label="Dealer Stock Number">
            <Input disabled={readOnly} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={24} lg={6}>
          <Form.Item
            name="year"
            label="Year"
            rules={[Validator.required("Year is required")]}
            normalize={value => value?.replace(/\D/g, "")?.slice(0, 4)}
          >
            <Input disabled={vinData?.year || disabledFields || readOnly} style={{ color: "black" }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={24} lg={6}>
          <Form.Item name="make" label="Make" rules={[Validator.required("Make is required")]}>
            <Input
              onChange={e => debouncedMake(e.target.value)}
              disabled={vinData?.make || disabledFields || readOnly}
              style={{ color: "black" }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={24} lg={6}>
          <Form.Item name="model" label="Model" rules={[Validator.required("Model is required")]}>
            <Input
              onChange={e => debouncedModel(e.target.value)}
              disabled={vinData?.model || disabledFields || readOnly}
              style={{ color: "black" }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col lg={6}>
          <Form.Item name="curbWeight" label="Curb Weight">
            <Input disabled={vinData?.curbWeight || disabledFields || readOnly} style={{ color: "black" }} />
          </Form.Item>
        </Col>
        <Col lg={6}>
          <Form.Item name="bodySubType" label="Body Sub Type">
            <Input disabled={vinData?.bodySubType || disabledFields || readOnly} style={{ color: "black" }} />
          </Form.Item>
        </Col>
        <Col lg={12}>
          <ExteriorDimensionsInputs
            disabledInputs={[
              vinData?.length || disabledFields || readOnly,
              vinData?.width || disabledFields || readOnly,
              vinData?.height || disabledFields || readOnly,
            ]}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col flex={1}>
          <Form.Item name="color" label="Color">
            <Select placeholder="Color" disabled={readOnly} getPopupContainer={trigger => trigger.parentElement}>
              {Object.values(VehicleColor).map(color => (
                <Select.Option key={color} value={color}>
                  {color}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        shouldUpdate={(prevValues, currentValues) => prevValues.color !== currentValues.color}
        noStyle // avoid empty space between form items
      >
        {({ getFieldValue }) => {
          const currentColor = getFieldValue("color");

          return currentColor === VehicleColor.Other ? (
            <Row gutter={16}>
              <Col xs={24} sm={24} md={24} lg={24}>
                <Form.Item
                  name="customColor"
                  label="Custom Color"
                  rules={[Validator.required("Please enter your custom color")]}
                >
                  <Input disabled={readOnly} />
                </Form.Item>
              </Col>
            </Row>
          ) : null;
        }}
      </Form.Item>

      <Row gutter={16}>
        <Col xs={24} sm={24} md={24} lg={18}>
          <Form.Item name="vehicleType" label="Vehicle Type" rules={[Validator.required("Vehicle Type is required")]}>
            <Select
              placeholder="Vehicle Type"
              getPopupContainer={trigger => trigger.parentElement}
              disabled={vinData?.vehicleType || disabledFields || readOnly}
            >
              {Object.keys(VehicleTypeMap).map(number => (
                <Select.Option key={number} value={parseInt(number, 10)}>
                  {VehicleTypeMap[number]}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={24} lg={6}>
          <Form.Item name="isEV" label="Is EV" valuePropName="checked">
            <Checkbox
              disabled={
                typeof vinData?.isEV === "boolean" ||
                disabledFields ||
                readOnly ||
                shipmentStatus >= ShipmentStatus.Delivered
              }
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        shouldUpdate={(prevValues, currentValues) => prevValues.vehicleType !== currentValues.vehicleType}
        noStyle // avoid empty space between form items
      >
        {({ getFieldValue }) => {
          const currentVehicleType = getFieldValue("vehicleType");

          return currentVehicleType === VehicleType.Truck ? (
            <Row gutter={16}>
              <Col lg={12}>
                <Form.Item name="truckWithLongWheelBase" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Checkbox disabled={disabledFields || readOnly}>Truck With Long Wheel Base</Checkbox>
                </Form.Item>
              </Col>
              <Col lg={12}>
                <Form.Item name="truckWithDualWheels" valuePropName="checked">
                  <Checkbox disabled={disabledFields || readOnly}>Truck With Dual Wheels</Checkbox>
                </Form.Item>
              </Col>
            </Row>
          ) : null;
        }}
      </Form.Item>

      <Form.Item
        shouldUpdate={(prevValues, currentValues) => prevValues.vehicleType !== currentValues.vehicleType}
        noStyle // avoid empty space between form items
      >
        {({ getFieldValue }) => {
          const currentVehicleType = getFieldValue("vehicleType");

          return currentVehicleType === VehicleType.Other ? (
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col flex={1}>
                <Form.Item
                  name="vehicleTypeComment"
                  label="Vehicle Type Comment"
                  disabled={disabledFields || readOnly}
                  dependencies={["vehicleType"]}
                >
                  <Input.TextArea rows={4} />
                </Form.Item>
              </Col>
            </Row>
          ) : null;
        }}
      </Form.Item>

      <Row gutter={16}>
        <Col flex={1}>
          <Form.Item name="comments" label="Additional information about the Vehicle">
            <Input.TextArea
              placeholder={`Examples: Lifted, extra-long body, missing tires, low ground clearance, won't start, won't jump, crashed, extra large tire rim package, dually, plow, extended cab, extended bed, (high, mid or low) roof, light bar, roof rack, service body, dump, high cube.

ANY NOTES ABOUT TIMING OR AVAILABILITY SHOULD GO INTO THE PICKUP OR DROPOFF NOTES FOR THE SHIPMENT.`}
              rows={6}
              disabled={disabledFields || readOnly || shipmentStatus >= ShipmentStatus.Delivered}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} sm={24} md={24} lg={24}>
          <Form.Item name="inoperational" label="Drivability" rules={[Validator.required("Drivability is required")]}>
            <Select disabled={disabledFields || readOnly} getPopupContainer={trigger => trigger.parentElement}>
              <Select.Option value={Drivability.RideAndDrive}>
                <Badge status="success" text="Ride and Drive" />
              </Select.Option>
              <Select.Option value={Drivability.Inoperational}>
                <Badge status="error" text="Inoperational" />
              </Select.Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <GatePassForm formRef={formRef} readOnly={readOnly} />

      <Form.Item name="msrp" hidden />

      {isPricingSectionVisible && <VehiclePricing formRef={formRef} shipmentData={requiredShipmentData} />}
    </Form>
  );
}
